import {
  FC,
  memo,
  useCallback,
  KeyboardEvent,
  ChangeEvent,
  FocusEvent,
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useRef,
} from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  acceptOnlyEnglish,
  formattedNumber,
  formattedString,
} from '../../../helpers/index';
import { IInput } from '../../../models';
import './styles.scss';
// Shared across all Input instances so Tab key from previous field is detected
let lastKeyPressedGlobal: string | null = null;
const Input: FC<IInput> = forwardRef<HTMLInputElement, IInput>(
  (
    {
      type,
      name,
      label,
      value = '',
      required = false,
      min,
      max,
      maxLength,
      minLength,
      onChange,
      onDebunceChange,
      onBlur,
      onFocus,
      onEnter,
      onKeyDown,
      onKeyUp,
      info,
      disabled,
      autoComplete = 'off',
      className = '',
      width,
      without_border = false,
      readOnly = false,
      placeholder,
      showArrow = false,
      decimal,
      pattern,
      textRight,
      numberSeparator,
      accept,
      showPassword: initialShowPassword = false,
      id,
      fieldType = '',
      setIsValid,
      tabIndex,
      autoFocus,
      mobileNumber,
      autoResize = false,
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false);
    const [changed, setChanged] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [formatedValue, setFormattedValue] = useState(value || '');
    const [inputValueDebounced, setInputValueDebounced] = useState(value || '');
    const [error, setError] = useState(false);
    const [showPassword, setShowPassword] = useState(initialShowPassword);
    const [dynamicWidth, setDynamicWidth] = useState<string | undefined>(width);

    const hiddenSpanRef = useRef<HTMLSpanElement | null>(null);
    const debouncedValue = useDebounce(inputValueDebounced, 700);

    // 🧠 Memoized input type
    const inputType = useMemo(() => {
      if (type === 'password') return showPassword ? 'text' : 'password';
      return numberSeparator && !focused ? 'text' : type;
    }, [type, showPassword, numberSeparator, focused]);

    const displayValue = useMemo(
      () => (focused ? inputValue : formatedValue),
      [inputValue, formatedValue, focused],
    );

    // 🔄 Sync external value
    useEffect(() => {
      const val = value ?? '';
      setInputValueDebounced(val);

      if (
        val !== '' &&
        val !== null &&
        val !== undefined &&
        (decimal || numberSeparator)
      ) {
        const str = formattedString(val, decimal);
        setInputValue(str);
        setFormattedValue(
          formattedNumber(Number(val), decimal, numberSeparator, type),
        );
      } else {
        setInputValue(val);
        setFormattedValue(val);
      }
    }, [value, decimal, numberSeparator, type]);

    // 🕒 Debounced change
    useEffect(() => {
      if (!onDebunceChange || debouncedValue === value || !changed || error)
        return;

      onDebunceChange(debouncedValue, name, fieldType, {
        target: { tagName: 'INPUT' },
      });
      setChanged(false);
    }, [
      debouncedValue,
      onDebunceChange,
      value,
      changed,
      error,
      name,
      fieldType,
    ]);

    // ✅ Range validation
    useEffect(() => {
      if (
        type === 'number' &&
        ((min !== undefined && +inputValue < min) ||
          (max !== undefined && +inputValue > max))
      ) {
        setError(true);
      } else {
        setError(false);
      }
    }, [inputValue, min, max, type]);

    // ✅ Pass validity to parent
    useEffect(() => {
      setIsValid?.(!error);
    }, [error, setIsValid]);

    // ⚡️ Auto resize logic
    const recalcWidth = useCallback(
      (text: string) => {
        if (!autoResize || !hiddenSpanRef.current) return;
        const span = hiddenSpanRef.current;
        span.textContent = text || placeholder || '';
        const newWidth = `${Math.max(span.offsetWidth + 12, 45)}px`;
        setDynamicWidth(newWidth);
      },
      [autoResize, placeholder],
    );

    useEffect(() => {
      if (autoResize) recalcWidth(displayValue as string);
    }, [displayValue, autoResize, recalcWidth]);

    // === Event handlers ===
    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setFocused(false);

        if (inputValue && (decimal || numberSeparator)) {
          setFormattedValue(
            formattedNumber(Number(inputValue), decimal, numberSeparator, type),
          );
        }

        if (required && !inputValue) {
          setError(true);
        }

        if (event.target.value !== value) {
          onBlur?.(event.target.value, name, fieldType, event);
        }
      },
      [
        inputValue,
        decimal,
        numberSeparator,
        type,
        value,
        onBlur,
        name,
        fieldType,
        required,
      ],
    );

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        onFocus?.(event.target.value, name, fieldType, event);
        if (lastKeyPressedGlobal === 'Tab') {
          lastKeyPressedGlobal = null;
          setTimeout(() => event.target.select(), 0);
        }
      },
      [onFocus, name, fieldType],
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        let val = event.target.value;

        if (type === 'number' && max !== undefined && +val > max) {
          val = String(max);
        }

        const formattedVal =
          decimal || numberSeparator
            ? formattedNumber(
                Number(val),
                decimal,
                numberSeparator,
                numberSeparator ? 'text' : type,
              )
            : val;

        const isError =
          (required && !val) ||
          (minLength && val.length < minLength) ||
          (pattern && !new RegExp(`^${pattern}$`).test(val));

        setInputValue(val);
        setFormattedValue(formattedVal);
        setInputValueDebounced(val);
        setError(!!isError);
        setChanged(true);

        if (!isError) {
          onChange?.(val, name, fieldType, event);
        }
      },
      [
        type,
        max,
        decimal,
        numberSeparator,
        minLength,
        pattern,
        required,
        onChange,
        name,
        fieldType,
      ],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        lastKeyPressedGlobal = event.key;
        if (event.key === 'Enter') {
          onEnter?.(inputValue, name, fieldType, event);
        }

        if (name === 'email' || name === 'username') {
          acceptOnlyEnglish(event);
        }

        if (mobileNumber) {
          const allowedChars = '0123456789-+(). ';
          const controlKeys = [
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'Tab',
            'Home',
            'End',
          ];

          if (
            !allowedChars.includes(event.key) &&
            !controlKeys.includes(event.key)
          ) {
            event.preventDefault();
          }
        }

        onKeyDown?.(inputValue, name, fieldType, event);
      },
      [inputValue, name, fieldType, onEnter, onKeyDown, mobileNumber],
    );

    const handleKeyUp = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        onKeyUp?.(inputValue, name, fieldType, event);
      },
      [inputValue, name, fieldType, onKeyUp],
    );

    const handleTogglePasswordVisibility = useCallback(() => {
      setShowPassword((prev) => !prev);
    }, []);

    // === Render ===
    return (
      <div
        className={`custom-input-group p-0 ${className} ${
          width ? 'w-fit-content' : 'w-100'
        } ${showArrow ? '' : 'hide-arrow'} ${
          textRight ? 'text-align-right' : ''
        } ${readOnly || disabled ? 'readOnly' : ''} ${
          error ? 'border-danger' : ''
        }`}
      >
        {label && (
          <label className={inputValue ? 'filled' : undefined} htmlFor={name}>
            {label}
            {required && <span className="required">*</span>}
          </label>
        )}

        <div className="input-wrapper">
          <input
            id={id}
            ref={ref}
            type={inputType}
            name={name}
            value={displayValue}
            required={required}
            aria-required={required}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            disabled={disabled}
            autoComplete={autoComplete}
            placeholder={placeholder}
            readOnly={readOnly}
            maxLength={maxLength}
            minLength={minLength}
            pattern={pattern}
            accept={accept}
            min={min}
            max={max}
            className={`native-input ${error ? 'error' : ''} ${
              without_border ? 'border-0' : ''
            }`}
            style={{
              width: autoResize ? dynamicWidth : width,
              transition: autoResize ? 'width 0.08s ease-out' : undefined,
            }}
            tabIndex={readOnly || disabled ? -1 : tabIndex}
            autoFocus={autoFocus}
          />

          {type === 'password' && (
            <button
              type="button"
              className="toggle-password-visibility"
              onClick={handleTogglePasswordVisibility}
            >
              <i className="icon-eye show-hide-password" />
            </button>
          )}
        </div>

        {info && (
          <small
            className={`form-text text-muted ${error ? 'enable-note' : ''}`}
          >
            {info}
          </small>
        )}

        {autoResize && (
          <span
            ref={hiddenSpanRef}
            className="invisible-span"
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'pre',
              fontSize: 'inherit',
              fontFamily: 'inherit',
              fontWeight: 'inherit',
            }}
          />
        )}
      </div>
    );
  },
);

export default memo(Input);
